import {createStyles} from '@mantine/core';
import React, {ReactNode, useEffect, useRef, useState} from 'react';

import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import ProcedureRunDesignFlowchart from 'studies/typed/shared/ProcedureRunDesignFlowchart';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import ProcedureRunMethodSelectDropdown from './ProcedureRunMethodSelectDropdown';

const LEFT_PANEL_SIZE = SidepanelSize.SMALL;
const RIGHT_PANEL_SIZE = SidepanelSize.MEDIUM;

interface ProcedureRunDesignFlowchartEditorProps {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  selectedMethodId: string | null;
  onChangeSelectedMethod: (methodId: string | null) => void;
  isInEditState: boolean;
  leftPanelChildren: ReactNode;
  rightPanelChildren: ReactNode;
}

export default function ProcedureRunDesignFlowchartEditor(
  props: ProcedureRunDesignFlowchartEditorProps
): JSX.Element {
  const {classes, cx} = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);
  const [selectedLeftSidebarIconIndex, setSelectedLeftSidebarIconIndex] = useState<number | null>(null);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);

  // Open left sidepanel when user enters edit mode
  useEffect(() => {
    if (!props.isInEditState) {
      return;
    }

    setIsLeftPanelOpen(true);
    setSelectedLeftSidebarIconIndex(0);
  }, [props.isInEditState]);

  return (
    <div
      ref={containerRef}
      className={classes.root}
      {...getTestProps({component: 'ProcedureRunDesignFlowchartEditor', element: 'root'})}
    >
      <FlowchartSidebar
        icons={[
          {
            name: 'create',
            onClick: () => {
              setIsLeftPanelOpen(true);
            },
          },
        ]}
        selectedIconIndex={selectedLeftSidebarIconIndex}
        setSelectedIconIndex={setSelectedLeftSidebarIconIndex}
      />
      <FlowchartContextPanel
        panelSide={PanelSide.LEFT}
        sidepanelName="Run Plan"
        isOpen={isLeftPanelOpen}
        onClose={() => {
          setIsLeftPanelOpen(false);
          setSelectedLeftSidebarIconIndex(null);
        }}
        size={LEFT_PANEL_SIZE}
        showExpandCollapseButtons={false}
      >
        {props.leftPanelChildren}
      </FlowchartContextPanel>
      <div className={cx(classes.flowchartContainer)}>
        <ProcedureRunDesignFlowchart
          conditions={props.conditions}
          methods={props.methods}
          controlsShiftLeft={0}
          onNodeClick={() => {
            // TODO (BNCH-153697): Guard on a selected method once click-to-select lands, so we don't
            // leave a stale isRightPanelOpen=true. A method is always selected today and there's no way
            // to unselect one, so the panel can't get queued open while selection is null.
            setIsRightPanelOpen(true);
          }}
        />
      </div>
      <FlowchartContextPanel
        panelSide={PanelSide.RIGHT}
        sidepanelName="Design"
        headerContent={
          props.selectedMethodId != null ? (
            <ProcedureRunMethodSelectDropdown
              methods={props.methods}
              selectedMethodId={props.selectedMethodId}
              onChangeSelectedMethod={props.onChangeSelectedMethod}
            />
          ) : undefined
        }
        isOpen={isRightPanelOpen && props.selectedMethodId != null}
        onClose={() => {
          setIsRightPanelOpen(false);
        }}
        size={RIGHT_PANEL_SIZE}
        showExpandCollapseButtons={true}
        resizable={true}
        resizeStorageKey="studies-procedure-design-right-sidepanel-width"
        resizeContainerRef={containerRef}
      >
        {props.rightPanelChildren}
      </FlowchartContextPanel>
    </div>
  );
}

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    position: 'absolute',
    height: '100%',
    width: '100%',
  },
  flowchartContainer: {
    flex: 1,
    position: 'relative',
    minWidth: 0,
  },
}));
